import React from 'react';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { useTranslation } from 'react-i18next';
import { Paper, Box, useTheme } from '@mui/material';
import { ScreenLockPortrait } from '@mui/icons-material/';
import { QSpinner } from '@kubiq-base/core';
import QRCode from 'react-qr-code';
import { useApi } from '../../state/ApiContext';
import CenteredQButton from '../Shared/CenteredQButton';
import HeaderWithDescription from '../Shared/HeaderWithDescription';
import MessageBox from '../Shared/MessageBoxes/MessageBox';
import FlexContainer from '../Shared/FlexContainers/FlexContainer';
import PageError from '../Shared/PageError';

type AuthData = { authenticatorKey: string; authenticatorUrl: string };

const LinkAuthenticator = (): JSX.Element => {
  const namespace = TranslationNamespaces.twoFA;
  const { t } = useTranslation(namespace);
  const theme = useTheme();
  const { getRequest } = useApi();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [authData, setAuthData] = React.useState<AuthData | undefined>();

  const bootstrapAsync = async (): Promise<void> => {
    await getRequest<{ data: AuthData }>('TwoFactorAuthentication/PairAuthenticator', undefined, setIsSubmitting).then(
      (result) => {
        if (result.data) setAuthData(result.data);
      },
    );
  };

  React.useEffect(() => {
    bootstrapAsync();
  }, []);

  return (
    <>
      {authData && (
        <HeaderWithDescription description='linkAuthenticatorDescription' translationNamespace={namespace} />
      )}
      {isSubmitting ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 72 }}>
          <QSpinner />
        </div>
      ) : (
        <>
          {authData ? (
            <FlexContainer>
              <MessageBox text={authData.authenticatorKey} icon={<ScreenLockPortrait />} />

              <Box sx={{ m: 3, display: 'flex', alignSelf: 'center' }}>
                <Paper
                  elevation={1}
                  sx={{ p: 3 }}
                  style={{
                    backgroundColor: '#FAFAFA',
                    border: `solid 1px ${theme.palette.border}`,
                    borderRadius: theme.shape.borderRadius,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <QRCode bgColor='#FAFAFA' value={authData.authenticatorUrl} size={250} />
                </Paper>
              </Box>

              <CenteredQButton to='/Enable2FA/2FA' color='secondary' fullWidth>
                {t('next')}
              </CenteredQButton>

              <CenteredQButton to='/' variant='text'>
                {t('cancel')}
              </CenteredQButton>
            </FlexContainer>
          ) : (
            <PageError code='401' message='notAuth' translationNamespace={namespace} />
          )}
        </>
      )}
    </>
  );
};

export default LinkAuthenticator;
